/* eslint-disable no-multi-spaces */
/** @jsx jsx */
import { jsx } from 'theme-ui';
import PropTypes from 'prop-types';
import GatsbyImage from 'gatsby-image';
import GatsbyBackgroundImage from 'gatsby-background-image';
import { readableColor, rgba } from 'polished';
import { colors } from '../../gatsby-plugin-theme-ui';
import imgalign from './image-alignment';
import spaceadjust from './space-adjustment';

const imageStyles = (mediaPosition) => {
  const bgImgStyles = {
    backgroundSize: 'auto 100% !important',
  };
  switch (mediaPosition) {
    case imgalign.BACKGROUND_FIXED_HEIGHT:
      return {
        height: ['273px', null, '448px'],
        '&:before, &:after': {
          ...bgImgStyles,
          backgroundPositionX: 'right !important',
        },
      };
    case imgalign.BACKGROUND_VARIABLE_HEIGHT:
      return {
        '&:before, &:after': {
          ...bgImgStyles,
          backgroundPositionX: [null, 'right !important'],
        },
      };
    default:
      return null;
  }
};

export const Container = ({
  mediaPosition, spaceAdjustment, bg, ...props
}) => {
  const ContainerComponent = mediaPosition && mediaPosition.includes('Background') ? GatsbyBackgroundImage : 'div';
  return (
    <ContainerComponent
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: ['column', 'row'],
        bg,
        color: readableColor(colors[bg || 'background'], colors.PrimaryDarkBlue, '#fff'),
        ...imageStyles(mediaPosition),
        pb: spaceAdjustment === spaceadjust.PAD_BOTTOM ? '4rem' : null,
      }}
      {...props}
    />
  );
};

Container.propTypes = {
  mediaPosition: PropTypes.string,
  spaceAdjustment: PropTypes.string,
  bg: PropTypes.string,
};

Container.defaultProps = {
  mediaPosition: null,
  spaceAdjustment: null,
  bg: null,
};

export const Title = (props) => (
  <div
    sx={{
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontSize: '2.2rem',
      lineHeight: 1.18,
      fontWeight: 'bold',
      mb: '7rem',
    }}
    {...props}
  />
);

const backgroundColors = (mediaPosition, bg) => {
  if (mediaPosition && mediaPosition.includes('Background')) {
    return {
      bg: rgba(colors[bg], 0.85),
    };
  }
  return null;
};

const backgroundPositions = (mediaPosition) => {
  if (mediaPosition) {
    const globalStyles = {
      // border: '6px solid magenta',
      pb: '10rem',
    };
    const backgroundStyles = {
      position: 'relative',
      top: 0,
      left: 0,
      minWidth: '375px',
    };
    switch (mediaPosition) {
      case 'Right':                        // CONTENT BLOCKS IN BODY WITH MEDIA
        return {
          ...globalStyles,
          // border: '9px solid yellow',
          flexBasis: '50%',
          pb: '0',
        };
      case 'Right - No Padding':           // CONTENT BLOCKS IN BODY WITH MEDIA, NO PADDING BELOW
        return {
          ...globalStyles,
          // border: '9px solid yellowgreen',
          flexBasis: '50%',
          pb: '0',
        };
      case 'Background - Full Width':      // MASTHEAD TITLE
        return {
          ...globalStyles,
          ...backgroundStyles,
          maxWidth: '556px',
          // border: '9px solid lime',
          pb: '10rem',
        };
      case 'Background - Fixed Height':    // USED ONCE IN A CONTENT BLOCK NOT ASSOCIATED WITH ANY PAGE
        return {
          ...globalStyles,
          ...backgroundStyles,
          // border: '9px solid crimson',
        };
      case 'Background - Flexible Height': // HOME PAGE VIDEO BLOCK
        return {
          ...globalStyles,
          ...backgroundStyles,
          maxWidth: ['100%', '50%'],
          // border: '9px solid orange',
        };
      default:
        return {
          ...globalStyles,
        };
    }
  }
  // return { borderBottom: '12px solid magenta' };
  return null;
};

export const Content = ({
  hasMedia, mediaPosition, bg, parentType, ...props
}) => (
  <div
    sx={{
      width: '100%',
      height: '100%',
      px: ['1.4rem', '2.8rem', '6.4rem'],
      pt: mediaPosition === imgalign.RIGHT_NO_PADDING || mediaPosition === imgalign.RIGHT ? '4rem' : '8rem',
      ...backgroundColors(mediaPosition, bg),
      ...backgroundPositions(mediaPosition),
      // nested content block font size is smaller
      fontSize: parentType === 'ContentBlock' ? '1.8rem' : null,
      lineHeight: parentType === 'ContentBlock' ? '2.6rem' : null,
      '> p, > h3, ul, ol': {
        maxWidth: hasMedia ? null : '100rem',
      },
      ul: {
        paddingLeft: '1.8rem',
      },
      // border: '2px solid red',
    }}
    {...props}
  />
);

Content.propTypes = {
  hasMedia: PropTypes.bool.isRequired,
  mediaPosition: PropTypes.string,
  bg: PropTypes.string,
  parentType: PropTypes.string,
};

Content.defaultProps = {
  mediaPosition: null,
  bg: null,
  parentType: null,
};

export const ImageContainer = ({ mediaPosition, ...props }) => (
  <div
    sx={{
      order: mediaPosition && mediaPosition.includes('Background') ? null : 1,
      flexBasis: mediaPosition === imgalign.RIGHT_NO_PADDING || mediaPosition === imgalign.RIGHT ? '50%' : null,
      pt: mediaPosition === imgalign.RIGHT_NO_PADDING || mediaPosition === imgalign.RIGHT ? '4.75rem' : '0',
    }}
    {...props}
  />
);

ImageContainer.propTypes = {
  mediaPosition: PropTypes.string,
};

ImageContainer.defaultProps = {
  mediaPosition: null,
};

export const Image = (props) => (
  <GatsbyImage
    sx={{
      // ...imageStyles(mediaPosition),
    }}
    {...props}
  />
);
